import React, { useEffect, useMemo, useState } from 'react';
import { useNostoContext } from '@srcDS/provider/nosto/NostoContext';
import styles from './NostoList.module.scss';
import { Heading } from '../../../typography/heading/Heading';
import NostoCartItem from './NostoCartItem';
import { useCartContext } from '@srcDS/provider/cart/CartContext';
import { parseProducts } from '@srcDS/provider/nosto/utils';
import { FORM_TYPE } from '../../../molecules/account/accountDropdown/AccountDropdown';
import showLoginErrorMessage from '../../../../utils/showLoginErrorMessage';
import { useOpenSignForm } from '@srcDS/utils/useOpenSignForm';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper/modules';
import { ArrowLeft, ArrowRight, CarretArrowLeft, CarretArrowRight } from '../../../atoms/icons';
import { nostoListsSliderOptions, theme } from './_vars';
import clsx from 'clsx';
import TagManager from '../../../../classes/TagManager';

const productsLimit = 4;

const NostoList = (props) => {
  const {
    asSlider = false,
    isEmptyCart,
    recentlyViewedPlacement,
    recentlyViewedTitle,
    recommenderPlacement,
    recommenderTitle,
    discounts,
    setShowDialog,
    setLoading,
    customerGroupId,
    setShowNostoBlock,
    loggedIn,
  } = props;
  const { getMiniCartProducts, hasNostoJs, nostoUpdateCart } = useNostoContext();
  const { signFormOpen } = useOpenSignForm();
  const { addToCart } = useCartContext();
  const [list, setList] = useState(null);
  const placement = isEmptyCart ? recentlyViewedPlacement : recommenderPlacement;
  const listTitle = isEmptyCart ? recentlyViewedTitle : recommenderTitle;
  const limitedList = list?.products?.slice(0, productsLimit);
  const products = parseProducts(limitedList) || [];

  useEffect(() => {
    if (hasNostoJs && placement) {
      getMiniCartProducts(placement, setList);
    }
  }, [hasNostoJs, isEmptyCart]);

  useEffect(() => {
    if (products?.length > 0) {
      setShowNostoBlock(true);
    }
  }, [products, list]);

  const handleAddToCart = async (product, selectedVariant, callback, errorCallback) => {
    addToCart(selectedVariant, 1, null, callback, true, errorCallback).then((data) => {
      if (hasNostoJs) {
        nostoUpdateCart(data);
      }

      TagManager.addToCart(product, selectedVariant, 1, data.cart, data.cart.currency);
    });
  };

  const content = useMemo(() => {
    if (asSlider) {
      return (
        <Swiper
          modules={[Navigation]}
          enabled={products?.length > 1}
          navigation={{
            prevEl: '.swiper-prev-arrow',
            nextEl: '.swiper-next-arrow',
            disabledClass: styles.hiddenClass,
          }}
          {...nostoListsSliderOptions}
        >
          {products?.map((item, idx) => {
            return (
              <SwiperSlide key={idx}>
                <NostoCartItem
                  key={idx}
                  item={item}
                  minimalView={asSlider}
                  discounts={discounts}
                  setShowDialog={setShowDialog}
                  setLoading={setLoading}
                  handleAddToCart={handleAddToCart}
                  loggedIn={loggedIn}
                  customerGroupId={customerGroupId}
                  toSignUp={() => {
                    setShowDialog(false);
                    signFormOpen(FORM_TYPE.LOGIN);
                    showLoginErrorMessage();
                  }}
                />
              </SwiperSlide>
            );
          })}
          <div className={clsx({ [styles.hideArrow]: products?.length <= 1 })}>
            <div
              data-carousel-arrow="miniCart_arrow_left"
              className={`swiper-prev-arrow ${styles.arrowNavigation} ${styles.arrowNavigationLeft}`}
            >
              {theme === 'Jag' ? <CarretArrowLeft /> : <ArrowLeft />}
            </div>
            <div
              data-carousel-arrow="miniCart_arrow_right"
              className={`swiper-next-arrow ${styles.arrowNavigation} ${styles.arrowNavigationRight}`}
            >
              {theme === 'Jag' ? <CarretArrowRight /> : <ArrowRight />}
            </div>
          </div>
        </Swiper>
      );
    }
    return products?.map((item, idx) => {
      return (
        <NostoCartItem
          key={idx}
          item={item}
          discounts={discounts}
          setShowDialog={setShowDialog}
          setLoading={setLoading}
          handleAddToCart={handleAddToCart}
          loggedIn={loggedIn}
          customerGroupId={customerGroupId}
          toSignUp={() => {
            setShowDialog(false);
            signFormOpen(FORM_TYPE.LOGIN);
            showLoginErrorMessage();
          }}
        />
      );
    });
  }, [list, discounts, loggedIn, products]);

  if (!products?.length) {
    return null;
  }

  return (
    <div className={clsx(styles.nostoContainer, { [styles.nostoContainerMini]: asSlider })}>
      {products?.length > 0 && (
        <>
          {list?.title && (
            <Heading as="h6" styleAs="h6" textTransform="uppercase" weight="light" className={styles.title}>
              {listTitle || list?.title}
            </Heading>
          )}
          {content}
        </>
      )}
    </div>
  );
};

export default NostoList;
